import * as Sentry from "@sentry/browser";
import { FieldSelectionPanel } from "./fieldSelectionPanel.js";
import { GeneratePlot } from "./plotGeneration.js";
import { setXAxisRange } from "./utils.js";
import { MapHandler } from "./weatherMaps.js";
import { showWelcomeModalIfNeeded } from "./welcomeModal.js";

Sentry.onLoad(() => {
	if (_CARGO.release !== "release") return;
	Sentry.init({
		dsn: "https://420f1bb54a49e09df70a70d4675d05f5@o4508378554105856.ingest.us.sentry.io/4508378569834496",
		release: `aws@${_CARGO.version}`,
		integrations: [
			Sentry.feedbackIntegration(),
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration(),
			Sentry.browserProfilingIntegration(),
			Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
		],
		environment: _CARGO.release === "release" ? "production" : "development",
		tracesSampleRate: 1.0,
		tracePropagationTargets: [
			"localhost",
			/^https:\/\/sbd\.australiaeast\.cloudapp\.azure\.com/,
			/^https:\/\/aws\.envlib\.com/,
		],
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
		profileSessionSampleRate: 1.0,
	});
});

const plotDivId = "myDiv";

const plot = new GeneratePlot(plotDivId, activeStation);

// Field-selection offcanvas, populated when opened
const fieldPanel = new FieldSelectionPanel((config) => {
	plot.applyFieldConfig(config);
});
document
	.getElementById("fieldSelectionOffcanvas")
	?.addEventListener("show.bs.offcanvas", () => {
		if (plot.tableId !== undefined) {
			fieldPanel.updateForContext(plot.tableId, plot.activeStation);
		}
	});

function downloadData(type: "json" | "csv"): void {
	// Use the current URL query params for date range — these are kept up to
	// date as ISO strings by plotGeneration's updateQueryString calls.
	// The Tera-injected `fromDate`/`toDate` globals are seconds-since-1990
	// integers and are NOT valid RFC3339, so the backend would reject them.
	const urlParams = new URLSearchParams(window.location.search);
	const currentFromDate = urlParams.get("from_date");
	const currentToDate = urlParams.get("to_date");

	const params = new URLSearchParams();
	if (plot.activeStation) params.set("station_id", plot.activeStation);
	if (currentFromDate) params.set("from_date", currentFromDate);
	if (currentToDate) params.set("to_date", currentToDate);

	const dl = document.createElement("a");
	dl.download = `weatherData.${type}`;
	dl.href = `/get_data/as_${type}?${params.toString()}`;
	dl.click();
	dl.remove();
}

//for date range buttons
async function loadDateRange(range: string): Promise<void> {
	const today = new Date();

	// Date range configuration lookup
	const dateRanges: Record<
		string,
		{ days?: number; months?: number; years?: number; label: string }
	> = {
		yesterday: { days: 1, label: "Past 24 Hours" },
		past_week: { days: 7, label: "Past Week" },
		past_month: { months: 1, label: "Past Month" },
		past_year: { years: 1, label: "Past Year" },
	};

	const config = dateRanges[range];
	if (!config) return;

	const from_date = new Date(today);
	if (config.days) from_date.setDate(from_date.getDate() - config.days);
	if (config.months) from_date.setMonth(from_date.getMonth() - config.months);
	if (config.years)
		from_date.setFullYear(from_date.getFullYear() - config.years);

	setXAxisRange(plot.divId, from_date.toISOString(), null);
	const dateRange = document.getElementById("dateRangeContent");
	if (dateRange) dateRange.innerText = config.label;
}

// Add event listeners for date range buttons
document.addEventListener("DOMContentLoaded", () => {
	showWelcomeModalIfNeeded(
		(
			window as Window & {
				bootstrap?: { Modal: new (element: Element) => { show(): void } };
			}
		).bootstrap,
	);
	document.querySelectorAll(".date-range-button").forEach((item) => {
		const element = item as HTMLAnchorElement;
		element.addEventListener("click", (event: MouseEvent) => {
			const target = event.target as HTMLAnchorElement | null;
			if (target) {
				const range = target.getAttribute("data-range");
				if (range) loadDateRange(range).catch((e) => console.error(e));
			}
		});
	});
	document.querySelectorAll(".download-button").forEach((item) => {
		const element = item as HTMLAnchorElement;
		element.addEventListener("click", (event) => {
			const target = event.target as HTMLAnchorElement | null;
			if (target) {
				const type = target.getAttribute("data-type") as "json" | "csv" | null;
				if (type) downloadData(type);
			}
		});
	});
	document.querySelectorAll(".stationLinks").forEach((item) => {
		const element = item as HTMLAnchorElement;
		element.addEventListener("click", (event) => {
			const target = event.target as HTMLAnchorElement | null;
			if (target) {
				const id = target.getAttribute("data-stationid");
				if (id) plot.setStation(id);
				plot.updatePlot(null, null).catch((e) => console.error(e));
			}
		});
	});
});

//initiate maps

const antMapHandler = new MapHandler("antMap", "Antarctic Stations");
const nzMapHandler = new MapHandler("nzMap", "New Zealand Stations");

const nzMapButton = document.getElementById("New Zealand StationsMapButton");
nzMapButton?.addEventListener("click", () => {
	nzMapHandler.showMap();
	antMapHandler.hideMap();
});

const antMapButton = document.getElementById("Antarctic StationsMapButton");
antMapButton?.addEventListener("click", () => {
	nzMapHandler.hideMap();
	antMapHandler.showMap();
});
antMapHandler.showMap();
